import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { BACKEND_URL } from '../constants';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import '../styles/OnboardingSurvey.css';

const FIGMA_DEMO_URL = 'https://www.figma.com/proto/VFZ9YuB9BbNNhyLgHh9T3j/Money-Sentinel?node-id=0-1&t=bk5flBmOPkVTsnGl-1';

const OnboardingSurvey = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get email and metadata from signup
  const surveyData = location.state || {};
  const userEmail = surveyData.email || '';
  const sourcePage = surveyData.source_page || '/';
  const sourceCampaign = surveyData.source_campaign || 'unknown';
  const priorityProblem = surveyData.priority_problem || '';
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [surveyStarted, setSurveyStarted] = useState(false);
  
  // Question 1: Account connection intent
  const [connectionIntent, setConnectionIntent] = useState('');
  
  // Question 2: Preferred connection types
  const [connectionTypes, setConnectionTypes] = useState({
    credit_cards: false,
    bank_accounts: false,
    savings_accounts: false,
    investment_accounts: false,
    email_receipts: false,
    other: false
  });
  const [otherConnectionType, setOtherConnectionType] = useState('');
  
  // Question 3: Pricing interest
  const [pricingInterest, setPricingInterest] = useState('');
  
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track survey start
  useEffect(() => {
    if (!surveyStarted && userEmail) {
      if (window.posthog) {
        window.posthog.capture('survey_started', {
          email: userEmail,
          source_page: sourcePage,
          source_campaign: sourceCampaign,
          priority_problem: priorityProblem
        });
      }
      setSurveyStarted(true);
    }
  }, [surveyStarted, userEmail, sourcePage, sourceCampaign, priorityProblem]);

  // Track step completions
  const trackStepCompletion = (step) => {
    if (window.posthog) {
      window.posthog.capture(`survey_step_${step}_completed`, {
        email: userEmail,
        source_page: sourcePage,
        source_campaign: sourceCampaign
      });
    }
  };

  // Handle demo click
  const handleDemoClick = () => {
    if (window.posthog) {
      window.posthog.capture('demo_clicked_after_survey', {
        email: userEmail,
        source_page: sourcePage
      });
    }
    window.open(FIGMA_DEMO_URL, '_blank');
  };

  // Navigate to next step
  const goToNextStep = () => {
    setIsTransitioning(true);
    trackStepCompletion(currentStep);
    
    setTimeout(() => {
      setCurrentStep(currentStep + 1);
      setIsTransitioning(false);
    }, 300);
  };

  // Handle Question 1 selection
  const handleConnectionIntentSelect = (value) => {
    setConnectionIntent(value);
  };

  // Handle Question 2 checkbox toggle
  const handleConnectionTypeToggle = (type) => {
    setConnectionTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Handle Question 3 selection
  const handlePricingSelect = (value) => {
    setPricingInterest(value);
  };

  // Submit survey
  const submitSurvey = async () => {
    setIsSubmitting(true);
    
    // Prepare connection types array
    const selectedTypes = Object.keys(connectionTypes).filter(key => connectionTypes[key]);
    
    const surveyResponse = {
      email: userEmail,
      source_page: sourcePage,
      source_campaign: sourceCampaign,
      priority_problem: priorityProblem,
      account_connection_intent: connectionIntent,
      preferred_connection_types: selectedTypes,
      preferred_connection_types_other: connectionTypes.other ? otherConnectionType : null,
      pricing_interest: pricingInterest,
      timestamp: new Date().toISOString()
    };
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/survey`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(surveyResponse),
      });

      if (!response.ok) {
        throw new Error('Failed to submit survey');
      }

      if (window.posthog) {
        window.posthog.capture('survey_completed', {
          email: userEmail,
          source_page: sourcePage,
          source_campaign: sourceCampaign,
          account_connection_intent: connectionIntent,
          pricing_interest: pricingInterest
        });
      }
      
      setIsTransitioning(true);
      setTimeout(() => {
        setIsCompleted(true);
        setIsTransitioning(false);
      }, 300);
      
    } catch (error) {
      console.error('Survey submission error:', error);
      
      // Save to localStorage as backup
      const pendingSurveys = JSON.parse(localStorage.getItem('pendingSurveys') || '[]');
      pendingSurveys.push(surveyResponse);
      localStorage.setItem('pendingSurveys', JSON.stringify(pendingSurveys));
      
      setIsTransitioning(true);
      setTimeout(() => {
        setIsCompleted(true);
        setIsTransitioning(false);
      }, 300);
    }
    
    setIsSubmitting(false);
  };

  // Track abandonment on unmount
  useEffect(() => {
    return () => {
      if (!isCompleted && surveyStarted && window.posthog) {
        window.posthog.capture('survey_abandoned', {
          email: userEmail,
          source_page: sourcePage,
          last_step: currentStep
        });
      }
    };
  }, [isCompleted, surveyStarted, currentStep, userEmail, sourcePage]);

  // Redirect if no email
  useEffect(() => {
    if (!userEmail) {
      navigate('/');
    }
  }, [userEmail, navigate]);

  if (!userEmail) {
    return null;
  }

  return (
    <div className="onboarding-survey">
      {!isCompleted ? (
        <>
          {/* Progress Indicator */}
          <div className="survey-progress-container">
            <div className="survey-progress-text">
              Step {currentStep} of 3
            </div>
            <div className="survey-progress-bar">
              <div 
                className="survey-progress-fill" 
                style={{ width: `${(currentStep / 3) * 100}%` }}
              />
            </div>
            <p className="survey-time-estimate">Just 3 quick questions (takes less than 20 seconds)</p>
          </div>

          {/* Question Cards */}
          <div className="survey-content">
            <div className={`survey-card-wrapper ${isTransitioning ? 'transitioning' : ''}`}>
              
              {/* Question 1 */}
              {currentStep === 1 && (
                <Card className="survey-card">
                  <CardContent className="survey-card-content">
                    <h2 className="survey-question-title">
                      How likely would you be to connect your financial accounts?
                    </h2>
                    <p className="survey-question-description">
                      MySentinel is designed to continuously monitor financial leaks, bill increases, 
                      subscription changes, and credit-related risks.
                    </p>
                    
                    <div className="survey-options">
                      {[
                        { value: 'definitely', label: 'Definitely' },
                        { value: 'probably', label: 'Probably' },
                        { value: 'maybe', label: 'Maybe' },
                        { value: 'unlikely', label: 'Unlikely' }
                      ].map(option => (
                        <button
                          key={option.value}
                          className={`survey-option ${connectionIntent === option.value ? 'selected' : ''}`}
                          onClick={() => handleConnectionIntentSelect(option.value)}
                        >
                          <div className="option-radio">
                            {connectionIntent === option.value && <div className="option-radio-selected" />}
                          </div>
                          <span className="option-label">{option.label}</span>
                        </button>
                      ))}
                    </div>
                    
                    <Button
                      onClick={goToNextStep}
                      disabled={!connectionIntent}
                      className="survey-next-btn"
                      size="lg"
                    >
                      Next
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Question 2 */}
              {currentStep === 2 && (
                <Card className="survey-card">
                  <CardContent className="survey-card-content">
                    <h2 className="survey-question-title">
                      Which accounts would you feel comfortable connecting first?
                    </h2>
                    <p className="survey-question-description">
                      Select all that apply.
                    </p>
                    
                    <div className="survey-options checkboxes">
                      {[
                        { key: 'credit_cards', label: 'Credit Cards' },
                        { key: 'bank_accounts', label: 'Bank Accounts' },
                        { key: 'savings_accounts', label: 'Savings Accounts' },
                        { key: 'investment_accounts', label: 'Investment Accounts' },
                        { key: 'email_receipts', label: 'Email Receipts' },
                        { key: 'other', label: 'Other' }
                      ].map(option => (
                        <button
                          key={option.key}
                          className={`survey-option checkbox ${connectionTypes[option.key] ? 'selected' : ''}`}
                          onClick={() => handleConnectionTypeToggle(option.key)}
                        >
                          <div className="option-checkbox">
                            {connectionTypes[option.key] && <CheckCircle2 className="checkbox-icon" />}
                          </div>
                          <span className="option-label">{option.label}</span>
                        </button>
                      ))}
                    </div>
                    
                    {connectionTypes.other && (
                      <div className="survey-other-input">
                        <input
                          type="text"
                          placeholder="Please specify"
                          value={otherConnectionType}
                          onChange={(e) => setOtherConnectionType(e.target.value)}
                          className="other-input"
                        />
                      </div>
                    )}
                    
                    <Button
                      onClick={goToNextStep}
                      disabled={!Object.values(connectionTypes).some(v => v)}
                      className="survey-next-btn"
                      size="lg"
                    >
                      Next
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Question 3 */}
              {currentStep === 3 && (
                <Card className="survey-card">
                  <CardContent className="survey-card-content">
                    <h2 className="survey-question-title">
                      If MySentinel continuously protected you from financial leaks, what would you consider paying?
                    </h2>
                    
                    <div className="survey-options">
                      {[
                        { value: '3', label: '£3/month' },
                        { value: '5', label: '£5/month' },
                        { value: '10', label: '£10/month' },
                        { value: '15', label: '£15/month' },
                        { value: 'not_willing', label: 'Not willing to pay' }
                      ].map(option => (
                        <button
                          key={option.value}
                          className={`survey-option ${pricingInterest === option.value ? 'selected' : ''}`}
                          onClick={() => handlePricingSelect(option.value)}
                        >
                          <div className="option-radio">
                            {pricingInterest === option.value && <div className="option-radio-selected" />}
                          </div>
                          <span className="option-label">{option.label}</span>
                        </button>
                      ))}
                    </div>
                    
                    <Button
                      onClick={submitSurvey}
                      disabled={!pricingInterest || isSubmitting}
                      className="survey-submit-btn"
                      size="lg"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </>
      ) : (
        /* Completion Screen */
        <div className="survey-completion">
          <Card className="completion-card">
            <CardContent className="completion-card-content">
              <div className="completion-icon-wrapper">
                <CheckCircle2 className="completion-icon" />
              </div>
              <h1 className="completion-title">Thank You!</h1>
              <p className="completion-description">
                Your feedback is helping us build a smarter financial watchdog.
              </p>
              
              <div className="completion-buttons">
                <Button
                  onClick={handleDemoClick}
                  className="completion-demo-btn"
                  size="lg"
                >
                  Try Interactive Demo
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="completion-home-btn"
                  size="lg"
                >
                  Back to Homepage
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default OnboardingSurvey;
